import { User } from "@shared/types/user";
import theme from "@shared/utils/themeColors";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Message } from "../types";
import MessageItem from "./MessageItem";

interface MessageListProps {
	messages: Message[];
	loading: boolean;
	hasMore: boolean;
	onLoadMore: () => Promise<boolean>;
	onDeleteMessage?: (messageId: string) => Promise<void>;
	onEditMessage?: (messageId: string, newContent: string) => Promise<void>;
	user?: User;
	selectedUser?: User;
	userProfiles: Record<string, User>;
	isGroup?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({
	messages,
	loading,
	hasMore,
	onLoadMore,
	onDeleteMessage,
	onEditMessage,
	user,
	selectedUser,
	userProfiles,
	isGroup,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const [isNearBottom, setIsNearBottom] = useState(true);
	const [newMessages, setNewMessages] = useState(0);
	const prevMessagesLengthRef = useRef(messages.length);
	const isInitialLoadRef = useRef(true);
	const isLoadingMoreRef = useRef(false);
	const lastMessageTimestampRef = useRef<number | null>(null);

	// Reset flags when user changes
	useEffect(() => {
		isInitialLoadRef.current = true;
		lastMessageTimestampRef.current = null;
		setNewMessages(0);
	}, [selectedUser]);

	// Scroll to bottom utility function with delay option
	const scrollToBottom = useCallback((delay = 0) => {
		if (delay > 0) {
			setTimeout(() => {
				messagesEndRef.current?.scrollIntoView();
			}, delay);
		} else {
			messagesEndRef.current?.scrollIntoView();
		}
	}, []);

	// Handle initial load scroll
	useEffect(() => {
		if (loading || !messages.length || !isInitialLoadRef.current) return;

		// Initial load should scroll to bottom with a small delay for media
		scrollToBottom();
		scrollToBottom(500);
		isInitialLoadRef.current = false;

		// Store the timestamp of the most recent message
		const lastMessage = messages[messages.length - 1];
		lastMessageTimestampRef.current = lastMessage.timestamp?.getTime() || null;
	}, [loading, messages, scrollToBottom]);

	// Handle scroll position and load more
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const handleScroll = async () => {
			const { scrollTop, scrollHeight, clientHeight } = container;

			// Check if near bottom
			const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
			const shouldBeNearBottom = distanceFromBottom < 100;

			if (shouldBeNearBottom !== isNearBottom) {
				setIsNearBottom(shouldBeNearBottom);
				if (shouldBeNearBottom) {
					setNewMessages(0);
				}
			}

			// Load more when near top and not already loading
			if (scrollTop < 200 && !isLoadingMoreRef.current && hasMore && !loading) {
				isLoadingMoreRef.current = true;

				const previousHeight = container.scrollHeight;
				const previousScrollTop = container.scrollTop;

				const loaded = await onLoadMore();

				if (loaded) {
					// Keep scroll position after loading older messages
					requestAnimationFrame(() => {
						const newScrollHeight = container.scrollHeight;
						const heightDiff = newScrollHeight - previousHeight;
						container.scrollTop = previousScrollTop + heightDiff;
					});
				}

				isLoadingMoreRef.current = false;
			}
		};

		container.addEventListener("scroll", handleScroll);
		return () => container.removeEventListener("scroll", handleScroll);
	}, [hasMore, isNearBottom, loading, onLoadMore]);

	// Handle messages updates
	useEffect(() => {
		if (loading || isInitialLoadRef.current) return;

		const newMessageCount = messages.length - prevMessagesLengthRef.current;

		if (newMessageCount > 0 && messages.length > 0) {
			const lastMessage = messages[messages.length - 1];
			const lastMessageTimestamp = lastMessage.timestamp?.getTime() || null;

			// Check if this is a new message (more recent than our last known message)
			const isNewMessage =
				lastMessageTimestampRef.current === null ||
				(lastMessageTimestamp !== null &&
					lastMessageTimestamp > lastMessageTimestampRef.current);

			if (isNewMessage) {
				const isOwnMessage = lastMessage.senderId === user?.id;

				// Scroll to bottom only for new messages when they're our own or we're near bottom
				if (isOwnMessage || isNearBottom) {
					const isMediaMessage =
						lastMessage.type === "image" || lastMessage.type === "video";
					scrollToBottom(isMediaMessage ? 500 : 100);
				} else {
					setNewMessages((prev) => prev + newMessageCount);
				}

				// Update the last known message timestamp
				lastMessageTimestampRef.current = lastMessageTimestamp;
			}
		}

		prevMessagesLengthRef.current = messages.length;
	}, [messages, user?.id, isNearBottom, loading, scrollToBottom]);

	const shouldShowDateSeparator = (currentMsg: Message, prevMsg?: Message) => {
		if (!prevMsg) return true;

		const currentDate = new Date(currentMsg.timestamp);
		const prevDate = new Date(prevMsg.timestamp);

		return (
			currentDate.getDate() !== prevDate.getDate() ||
			currentDate.getMonth() !== prevDate.getMonth() ||
			currentDate.getFullYear() !== prevDate.getFullYear()
		);
	};

	const renderMessages = () => {
		return messages
			.map((message, index) => {
				const elements = [];
				const prevMessage = messages[index - 1];

				if (shouldShowDateSeparator(message, prevMessage)) {
					elements.push(
						<DateSeparator
							key={`date-${message.id}`}
							date={message.timestamp}
						/>
					);
				}

				const senderProfile = userProfiles[message.senderId];
				elements.push(
					<MessageItem
						key={message.id}
						message={message}
						senderProfile={senderProfile}
						isGroup={isGroup}
						onDelete={async (messageId) => {
							try {
								await onDeleteMessage?.(messageId);
							} catch (error) {
								console.error("Error deleting message:", error);
							}
						}}
						onEdit={async (messageId, newContent) => {
							try {
								await onEditMessage?.(messageId, newContent);
							} catch (error) {
								console.error("Error editing message:", error);
							}
						}}
					/>
				);

				return elements;
			})
			.flat();
	};

	return (
		<div className="grow overflow-y-auto px-4" ref={containerRef}>
			{/* Loading indicator for more messages */}
			{loading && hasMore && (
				<div className="flex justify-center py-2">
					<div
						className={`animate-spin rounded-full h-5 w-5 border-b-2 ${theme.border.brand}`}
					></div>
				</div>
			)}
			{renderMessages()}
			<div ref={messagesEndRef} />

			{/* New messages indicator */}
			{newMessages > 0 && !isNearBottom && (
				<button
					onClick={() => {
						scrollToBottom();
						setNewMessages(0);
					}}
					className={`fixed bottom-24 left-1/2 -translate-x-1/2 ${theme.bg.brand} text-white px-4 py-2 rounded-full shadow-lg hover:bg-violet-600 transition-colors`}
				>
					{newMessages} new {newMessages === 1 ? "message" : "messages"}
				</button>
			)}
		</div>
	);
};

interface DateSeparatorProps {
	date: Date;
}

const DateSeparator: React.FC<DateSeparatorProps> = ({ date }) => {
	const formattedDate = new Intl.DateTimeFormat("en-US", {
		weekday: "long",
		day: "numeric",
		month: "long",
	}).format(date);

	return (
		<div className="flex justify-center">
			<div
				className={`inline-flex items-center justify-center text-xs ${theme.text.secondary} font-medium px-2.5 py-1 bg-white dark:bg-gray-700 shadow-sm rounded-full my-5`}
			>
				{formattedDate}
			</div>
		</div>
	);
};

export default MessageList;
